<script setup lang="ts">
import { cn } from "@/lib/utils";
import { eventBus } from "#bb/lib/eventBus";
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
import { buildAssessmentAnswerPayload } from "#bb/lib/assessmentAnswers";

const props = defineProps<{ id: string }>();
const emit = defineEmits<{
  (e: "content-update", value: string): void;
  (e: "answer-error", payload: { id: string; error: unknown }): void;
}>();

const { getContentById, getResolvedDataPath } = useContentManager();
const definitionData = computed(() => getContentById(props.id));
const features = inject("render-features", { editor: false });

const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Building block editors",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit the options",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Radio",
    icon: "lucide:list-collapse",
  },
});

const route = useRoute();
const { getAnswer, getCachedAnswer, setAnswer } = useAnswers();

const answer = ref<string>("");
const answerPath = computed(() => getResolvedDataPath(props.id).join("/"));

// Reactively mirror the shared answer cache so a selection made on any surface (the page or the AI
// chat) is reflected here live; getAnswer() ensures the cache has been loaded at least once.
watch(
  () => getCachedAnswer(props.id, answerPath.value)?.value,
  (value) => {
    const next = (value ?? "") as string;
    if (next !== answer.value) {
      answer.value = next;
    }
  },
  { immediate: true },
);

onMounted(() => {
  if (props.id) {
    void getAnswer(props.id, answerPath.value);
  }
});

const { error, validate } = useFormField({
  id: props.id,
  componentId: ComponentDefinitionIds.RADIO_GROUP,
  getValue: () => answer.value,
  getValidation: () => definitionData.value?.validation,
  inputType: "string",
});

const save = async () => {
  if (validate().ok === false || !props.id || !definitionData.value) {
    return;
  }

  try {
    await setAnswer(
      props.id,
      buildAssessmentAnswerPayload({
        block: definitionData.value,
        componentId: ComponentDefinitionIds.RADIO_GROUP,
        value: answer.value,
        path: answerPath.value,
      }),
      { throwOnError: true },
    );
    emit("content-update", answer.value);
  } catch (error) {
    emit("answer-error", { id: props.id, error });
  }
};
</script>

<template>
  <div :class="cn('relative space-y-4', $attrs.class ?? '')">
    <fieldset
      :aria-label="definitionData.id"
      :aria-invalid="!!error"
      class="-space-y-px rounded-md bg-card text-card-foreground"
      :class="error ? 'ring-1 ring-destructive rounded-md' : ''"
      :disabled="$attrs.disabled"
    >
      <label
        v-for="option in definitionData.options"
        :key="option.id"
        :aria-label="option.name"
        class="group flex cursor-pointer border border-border bg-card p-4 transition-colors first:rounded-tl-md first:rounded-tr-md last:rounded-br-md last:rounded-bl-md focus:outline-none has-checked:relative has-checked:z-10 has-checked:border-primary has-checked:bg-primary/15"
      >
        <input
          v-model="answer"
          @change="save"
          type="radio"
          :value="option.id"
          class="relative mt-0.5 size-4 shrink-0 appearance-none rounded-full border border-input bg-background before:absolute before:inset-1 before:rounded-full before:bg-primary-foreground not-checked:before:hidden checked:border-primary checked:bg-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:border-input disabled:bg-muted disabled:opacity-50 disabled:before:bg-muted-foreground forced-colors:appearance-auto forced-colors:before:hidden"
        />
        <span class="ml-3 flex flex-col">
          <span
            class="block text-sm font-medium text-foreground"
          >
            {{ option.name }}
          </span>
          <span
            class="block text-sm text-muted-foreground"
          >
            {{ option.description }}
          </span>
        </span>
      </label>
    </fieldset>
    <p v-if="error" class="text-destructive text-sm">{{ error }}</p>
  </div>
</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
export default {
  componentId: ComponentDefinitionIds.RADIO_GROUP,
  icon: "lucide:list-collapse",
  editor: () => import("#bb/components/editor/assessment/radio.vue"),
  type: "Radio Group",
  description: "Select a single option from a list of choices",
  default: {
    options: [
      { id: crypto.randomUUID(), name: "Option A", value: 1, description: "" },
      { id: crypto.randomUUID(), name: "Option B", value: 2, description: "" },
      { id: crypto.randomUUID(), name: "Option C", value: 3, description: "" },
    ],
    value: "",
  },
  categories: ["Assessment"],
  order: 10002,
};
</script>
